import { Injectable } from '@angular/core';

import { Selector, StateContext, Action, State } from '@ngxs/store';

import { Terminal } from '@app/modules/shared/models/terminal.model';
import { COMMON_TERMINALS } from '@shared/const/terminals.consts';

import {
  SetTerminalsAction,
  RemoveTerminalsAction,
  SetPassengersAction,
  RemovePassengersAction,
  SetFlightNumberAction,
  RemoveFlightNumberAction,
  SetShipAction,
  RemoveShipAction,
  SetExtrasAction,
  RemoveExtrasAction,
  SetCommonTerminalsAction,
  SetSourceModeAction,
  RemoveTripAction,
  SetSelectedUpAction,
  SetIsMasBlauAction,
  SetIsModifyReservationAction,
  SetAgentTokenAction,
  SetAppModeAction,
  SetClientAppModeAction, SetPromoCodeAction,
} from './trip.actions';
import { SourceAndQueryParams, TripStateModel } from './trip.model';

const INITIAL_STATE: TripStateModel = {
  in_terminal: null,
  common_in_terminal: null,
  out_terminal: null,
  common_out_terminal: null,
  in_passengers: null,
  out_passengers: null,
  flight_number: null,
  ship: null,
  extras: null,
  source_mode: null,
  app_mode: false,
  client_app_mode: false,
  id_lot: null,
  promocode: null,
  promocode_request_timestamp: null,
};
@State<TripStateModel>({
  name: 'trip',
  defaults: INITIAL_STATE,
})
@Injectable()
export class TripState {
  @Selector()
  static trip(state: TripStateModel): TripStateModel {
    return state;
  }

  @Selector()
  static sourceMode(state: TripStateModel): string {
    return state.source_mode;
  }

  @Selector()
  static idLot(state: TripStateModel): number {
    return state.id_lot;
  }

  @Selector()
  static passengers(state: TripStateModel): Record<string, number> {
    const passengers = {
      in_passengers: state.in_passengers,
      out_passengers: state.out_passengers,
    };
    return passengers;
  }

  @Selector()
  static terminals(state: TripStateModel): Record<string, number> {
    const terminals = {
      in_terminal: state.in_terminal,
      out_terminal: state.out_terminal,
    };
    return terminals;
  }

  @Selector()
  static sourceAndQueryParams(state: TripStateModel): SourceAndQueryParams {
    const {
      source_mode,
      selected_up,
      is_mb,
      is_modify_reservation,
      is_agent_token,
      app_mode,
      client_app_mode,
      promocode,
      promocode_request_timestamp
    } = state;
    return {
      source_mode: source_mode,
      selected_up: selected_up,
      is_mb: is_mb,
      is_modify_reservation: is_modify_reservation,
      is_agent_token: is_agent_token,
      app_mode: app_mode,
      client_app_mode: client_app_mode,
      promocode: promocode,
      promocode_request_timestamp: promocode_request_timestamp
    };
  }

  @Action(SetTerminalsAction)
  setTerminalsAction(
    ctx: StateContext<TripStateModel>,
    action: SetTerminalsAction
  ): void {
    ctx.patchState({
      in_terminal: action.inTerminal,
      out_terminal: action.outTerminal,
    });
  }

  @Action(SetCommonTerminalsAction)
  setCommonTerminalsAction(
    ctx: StateContext<TripStateModel>,
    action: SetCommonTerminalsAction
  ): void {
    const id_lot = COMMON_TERMINALS.find(
      (terminal: Terminal) =>
        terminal.commonTerminalId === action.commonInTerminal
    )?.idLot;
    ctx.patchState({
      common_in_terminal: action.commonInTerminal,
      common_out_terminal: action.commonOutTerminal,
      id_lot: id_lot,
    });
  }

  @Action(SetPromoCodeAction)
  setPromoCodeAction(
    ctx: StateContext<TripStateModel>,
    action: SetPromoCodeAction
  ): void {
    ctx.patchState({
      promocode: action.promoCode,
      promocode_request_timestamp: action.promoCodeRequestTimestamp
    });
  }

  @Action(SetSourceModeAction)
  setSourceModeAction(
    ctx: StateContext<TripStateModel>,
    action: SetSourceModeAction
  ): void {
    ctx.patchState({
      source_mode: action.sourceMode,
    });
  }

  @Action(SetAppModeAction)
  setAppModeAction(
    ctx: StateContext<TripStateModel>,
    action: SetAppModeAction
  ): void {
    ctx.patchState({
      app_mode: action.appMode,
    });
  }

  @Action(SetClientAppModeAction)
  setClientAppModeAction(
    ctx: StateContext<TripStateModel>,
    action: SetClientAppModeAction
  ): void {
    ctx.patchState({
      client_app_mode: action.clientAppMode,
    });
  }

  @Action(SetAgentTokenAction)
  setAgentTokenAction(
    ctx: StateContext<TripStateModel>,
    action: SetAgentTokenAction
  ): void {
    ctx.patchState({
      is_agent_token: action.is_agent_token,
    });
  }

  @Action(SetSelectedUpAction)
  setSelectedUpAction(
    ctx: StateContext<TripStateModel>,
    action: SetSelectedUpAction
  ): void {
    ctx.patchState({
      selected_up: action.urbanParking,
    });
  }

  @Action(SetIsMasBlauAction)
  setIsMasBlauAction(
    ctx: StateContext<TripStateModel>,
    action: SetIsMasBlauAction
  ): void {
    ctx.patchState({
      is_mb: action.isMasBlau,
    });
  }

  @Action(SetIsModifyReservationAction)
  setIsModifyReservationAction(
    ctx: StateContext<TripStateModel>,
    action: SetIsModifyReservationAction
  ): void {
    ctx.patchState({
      is_modify_reservation: action.isModify,
    });
  }

  @Action(RemoveTerminalsAction)
  removeTerminalsAction(ctx: StateContext<TripStateModel>): void {
    ctx.patchState({
      in_terminal: INITIAL_STATE.in_terminal,
      out_terminal: INITIAL_STATE.out_terminal,
    });
  }

  @Action(SetPassengersAction)
  setPassengersAction(
    ctx: StateContext<TripStateModel>,
    action: SetPassengersAction
  ): void {
    ctx.patchState({
      in_passengers: action.inPassengers,
      out_passengers: action.outPassengers,
    });
  }

  @Action(RemovePassengersAction)
  removePassengersAction(ctx: StateContext<TripStateModel>): void {
    ctx.patchState({
      in_passengers: INITIAL_STATE.in_passengers,
      out_passengers: INITIAL_STATE.out_passengers,
    });
  }

  @Action(SetFlightNumberAction)
  setFlightNumberAction(
    ctx: StateContext<TripStateModel>,
    action: SetFlightNumberAction
  ): void {
    ctx.patchState({ flight_number: action.flightNumber });
  }

  @Action(RemoveFlightNumberAction)
  removeFlightNumberAction(ctx: StateContext<TripStateModel>): void {
    ctx.patchState({ flight_number: INITIAL_STATE.flight_number });
  }

  @Action(SetShipAction)
  setShipAction(
    ctx: StateContext<TripStateModel>,
    action: SetShipAction
  ): void {
    ctx.patchState({ ship: action.ship });
  }

  @Action(RemoveShipAction)
  removeShipAction(ctx: StateContext<TripStateModel>): void {
    ctx.patchState({ ship: INITIAL_STATE.ship });
  }

  @Action(SetExtrasAction)
  setExtrasAction(
    ctx: StateContext<TripStateModel>,
    action: SetExtrasAction
  ): void {
    ctx.patchState({ extras: action?.extras });
  }

  @Action(RemoveExtrasAction)
  removeExtrasAction(ctx: StateContext<TripStateModel>): void {
    ctx.patchState({ extras: INITIAL_STATE.extras });
  }

  @Action(RemoveTripAction)
  removeTripAction(ctx: StateContext<TripStateModel>): void {
    ctx.patchState({
      in_terminal: null,
      //common_in_terminal: null,
      out_terminal: null,
      common_out_terminal: null,
      in_passengers: null,
      out_passengers: null,
      flight_number: null,
      ship: null,
      extras: null,
    });
  }
}
